import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Users, UserPlus, Mail, Phone, CheckCircle, XCircle,
  Search, Filter, ChevronDown, MoreVertical, Shield,
  Video, FileText, Star, Clock, Building
} from 'lucide-react';
import AdminLayout from './AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPonentes() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [speakers, setSpeakers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: ''
  });
  const [error, setError] = useState('');

  const token = localStorage.getItem('upim_token');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    loadSpeakers();
  }, [navigate, token]);

  const loadSpeakers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/content/admin/speakers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSpeakers(response.data.speakers || []);
    } catch (error) {
      console.error('Error loading speakers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSpeaker = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const form = new FormData();
      form.append('name', formData.name);
      form.append('email', formData.email);
      form.append('password', formData.password);
      if (formData.phone) form.append('phone', formData.phone);

      await axios.post(`${API_URL}/api/content/admin/speakers/create`, form, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setShowAddModal(false);
      setFormData({ name: '', email: '', password: '', phone: '' });
      loadSpeakers();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al crear ponente');
    }
  };

  const handleVerifySpeaker = async (userId) => {
    try {
      await axios.put(
        `${API_URL}/api/content/admin/speakers/${userId}/verify`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadSpeakers();
    } catch (error) {
      console.error('Error verifying speaker:', error);
    }
  };

  const filteredSpeakers = speakers.filter(s => 
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="p-6" data-testid="admin-ponentes-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Users className="w-7 h-7 text-purple-500" />
              Gestión de Ponentes
            </h1>
            <p className="text-gray-500 text-sm">Administra los presentadores y speakers de webinars</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors"
            data-testid="add-speaker-btn"
          >
            <UserPlus className="w-4 h-4" />
            Agregar Ponente
          </button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nombre o email..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              data-testid="search-speakers-input"
            />
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-xl border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{speakers.length}</p>
                <p className="text-sm text-gray-500">Total Ponentes</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {speakers.filter(s => s.profile?.is_verified).length}
                </p>
                <p className="text-sm text-gray-500">Verificados</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Video className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {speakers.reduce((acc, s) => acc + (s.profile?.total_webinars || 0), 0)}
                </p>
                <p className="text-sm text-gray-500">Webinars Totales</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Star className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {speakers.length > 0 
                    ? (speakers.filter(s => s.is_active).length / speakers.length * 100).toFixed(0) 
                    : 0}%
                </p>
                <p className="text-sm text-gray-500">Activos</p>
              </div>
            </div>
          </div>
        </div>

        {/* Speakers Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-gray-500">Cargando ponentes...</div>
          ) : filteredSpeakers.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No hay ponentes registrados</p>
              <button
                onClick={() => setShowAddModal(true)}
                className="mt-3 text-purple-500 hover:text-purple-600 font-medium"
              >
                Agregar el primer ponente
              </button>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Ponente</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Contacto</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Estado</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Webinars</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredSpeakers.map(speaker => (
                  <tr key={speaker.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                          <span className="text-purple-600 font-semibold">
                            {speaker.name?.charAt(0).toUpperCase() || 'P'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{speaker.name}</p>
                          {speaker.profile?.title && (
                            <p className="text-sm text-gray-500">{speaker.profile.title}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <p className="text-sm text-gray-600 flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {speaker.email}
                        </p>
                        {speaker.phone && (
                          <p className="text-sm text-gray-500 flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {speaker.phone}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {speaker.profile?.is_verified ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                            <CheckCircle className="w-3 h-3" /> Verificado
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full">
                            <Clock className="w-3 h-3" /> Pendiente
                          </span>
                        )}
                        {!speaker.is_active && (
                          <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                            Inactivo
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-900 font-medium">
                        {speaker.profile?.total_webinars || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {!speaker.profile?.is_verified && (
                          <button
                            onClick={() => handleVerifySpeaker(speaker.id)}
                            className="px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-sm rounded-lg transition-colors"
                            data-testid={`verify-speaker-${speaker.id}`}
                          >
                            Verificar
                          </button>
                        )}
                        <button className="p-1.5 text-gray-400 hover:text-gray-600">
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Add Speaker Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-md">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-purple-500" />
                    Agregar Nuevo Ponente
                  </h3>
                  <button 
                    onClick={() => setShowAddModal(false)} 
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <form onSubmit={handleCreateSpeaker} className="p-6 space-y-4">
                {error && (
                  <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg">
                    {error}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Lic. Juan Pérez"
                    required
                    data-testid="speaker-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="ponente@email.com"
                    required
                    data-testid="speaker-email-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contraseña *
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Mínimo 8 caracteres"
                    minLength={8}
                    required
                    data-testid="speaker-password-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="+52 55 1234 5678"
                    data-testid="speaker-phone-input"
                  />
                </div>

                <div className="bg-purple-50 p-3 rounded-lg text-sm text-purple-700">
                  <p className="font-medium">El ponente recibirá:</p>
                  <ul className="mt-1 list-disc list-inside text-purple-600">
                    <li>Acceso al portal de ponentes</li>
                    <li>Capacidad de subir materiales</li>
                    <li>Guía PDF con instrucciones</li>
                  </ul>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600"
                    data-testid="create-speaker-btn"
                  >
                    Crear Ponente
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
